import {noop} from '@github-ui/noop'
import {testIdProps} from '@github-ui/test-id-props'
import {useSyncedState} from '@github-ui/use-synced-state'
import {Heading} from '@primer/react'
import {clsx} from 'clsx'
import {
  Children,
  type MutableRefObject,
  type PropsWithChildren,
  type ReactElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import {defaultVariant} from '../constants'
import {useRovingTabIndex} from '../hooks/use-roving-tab-index'
import type {PrefixedStylableProps} from '../types'
import {IdProvider, useListViewId} from './IdContext'
import {ItemsProvider} from './ItemsContext'
import styles from './ListView.module.css'
import type {ListViewMetadata} from './Metadata'
import {MultiPageSelectionProvider} from './MultiPageSelectionContext'
import {type SelectionContextProps, SelectionProvider} from './SelectionContext'
import {TitleProvider, type TitleProviderProps, useListViewTitle} from './TitleContext'
import {useListViewVariant, VariantProvider, type VariantType} from './VariantContext'

export type ListViewProps = PropsWithChildren<{
  /**
   * An optional element to contain bulk actions, a 'Select all' checkbox, a density toggle for the view, etc.
   */
  metadata?: ReactElement<typeof ListViewMetadata>
  /**
   * Controls the width and height of the list and its contents
   */
  variant?: VariantType
  /**
   * Callback for when the variant changes
   */
  onVariantChange?: (variant: VariantType) => void
  /**
   * Controls item's selection mode; bulk actions are allowed if true. Defaults to not selectable.
   */
  isSelectable?: boolean
  /**
   * Controls if a drag handle is shown. Defaults to no drag handles.
   */
  hasDragHandle?: boolean
  /**
   * What a single list item should be called. Used to customize assistive text about how many list items are
   * selected. Defaults to 'list item'.
   */
  singularUnits?: string
  /**
   * What many list items are called. Used to customize assistive text about how many list items are selected.
   * Defaults to 'list items'.
   */
  pluralUnits?: string
  /**
   * Optional ID of an element that labels the list view. When provided, this takes the place of the default sr-only title.
   */
  ariaLabelledBy?: string
  /**
   * Content to display when the list is empty. When provided and there are no items,
   * this will be rendered instead of an empty list. Consumers have full control over
   * the empty state rendering, typically using Primer's BlankSlate component.
   *
   * @example
   * ```tsx
   * <ListView
   *   emptyState={
   *     <BlankSlate>
   *       <BlankSlate.Visual>
   *         <SearchIcon size={32} />
   *       </BlankSlate.Visual>
   *       <BlankSlate.Heading>No items found</BlankSlate.Heading>
   *       <BlankSlate.Description>
   *         Try adjusting your search or filter criteria.
   *       </BlankSlate.Description>
   *     </BlankSlate>
   *   }
   * >
   *   {items}
   * </ListView>
   * ```
   */
  emptyState?: React.ReactNode
  className?: string
  listRef?: MutableRefObject<HTMLUListElement | undefined>
  /**
   * Optional property that passes through to the `strict` prop in the useFocusZone hook in @primer/behaviors.
   * When true, performs additional checks to determine tabbability which may adversely affect app performance.
   * Defaults to `true`
   */
  strictFocusZone?: boolean
  /**
   * Component type to render for drag and drop container
   */
  as?: React.ElementType

  role?: string
}> &
  Omit<TitleProviderProps, 'children'> &
  Pick<SelectionContextProps, 'totalCount'> &
  Partial<Pick<SelectionContextProps, 'selectedCount'>> &
  PrefixedStylableProps<'itemsList'>

export const ListView = ({
  children,
  hasDragHandle,
  isSelectable,
  onVariantChange = noop,
  pluralUnits,
  selectedCount = 0,
  singularUnits,
  title,
  titleHeaderTag,
  totalCount,
  variant: externalVariant = defaultVariant,
  ...rest
}: PropsWithChildren<ListViewProps>): React.ReactElement => {
  const [variant, setVariant] = useSyncedState(externalVariant)

  useEffect(() => {
    onVariantChange?.(variant)
  }, [onVariantChange, variant])

  // eslint-disable-next-line @eslint-react/no-children-to-array
  const countOnPage = useMemo(() => Children.toArray(children).length, [children])

  return (
    <IdProvider>
      <TitleProvider title={title} titleHeaderTag={titleHeaderTag}>
        <VariantProvider variant={variant} setVariant={setVariant}>
          <SelectionProvider
            countOnPage={countOnPage}
            selectedCount={selectedCount}
            totalCount={totalCount}
            singularUnits={singularUnits}
            pluralUnits={pluralUnits}
            isSelectable={isSelectable}
            hasDragHandle={hasDragHandle}
          >
            <MultiPageSelectionProvider>
              <ItemsProvider>
                <ListViewContainer {...rest}>{children}</ListViewContainer>
              </ItemsProvider>
            </MultiPageSelectionProvider>
          </SelectionProvider>
        </VariantProvider>
      </TitleProvider>
    </IdProvider>
  )
}

const ListViewContainer = ({
  ariaLabelledBy: externalAriaLabelledBy,
  as,
  children,
  className,
  emptyState,
  itemsListClassName,
  itemsListStyle: style,
  listRef,
  metadata,
  role,
  strictFocusZone,
  ...rest
}: Omit<ListViewProps, 'title' | 'titleHeaderTag'>): React.ReactElement => {
  const {idPrefix} = useListViewId()
  const {title, titleHeaderTag} = useListViewTitle()
  const {variant} = useListViewVariant()
  const {containerRef} = useRovingTabIndex(strictFocusZone) as {containerRef: MutableRefObject<HTMLUListElement>}
  const [hasListItems, setHasListItems] = useState(true)

  useEffect(() => {
    if (listRef) {
      listRef.current = containerRef.current
    }
  }, [containerRef, listRef])

  // For accessibility, track if there are <li> elements inside the list to render appropriate roles and attributes
  useLayoutEffect(() => {
    if (containerRef.current) {
      const hasTopLevelItems = Array.from(containerRef.current.querySelectorAll('li')).some(li => {
        let parent = li.parentElement
        while (parent && parent !== containerRef.current) {
          if (parent.tagName === 'UL' || parent.tagName === 'OL') return false
          parent = parent.parentElement
        }
        return true
      })

      setHasListItems(hasTopLevelItems)
    }
  }, [containerRef, children])

  const listViewContainerTitleId = externalAriaLabelledBy ?? `${idPrefix}-list-view-container-title`

  // Determine if we should show the empty state
  const showEmptyState = emptyState && !hasListItems

  // A container element for an unordered list, <ul>, should not exist without the list items, <li>.
  // When showing empty state, use div to avoid semantic list without list items
  const As = as ? as : hasListItems ? 'ul' : 'div'

  return (
    <div id={`${idPrefix}-list-view-container`} className={clsx(styles.container, className)}>
      {!externalAriaLabelledBy && (
        <Heading
          className="sr-only"
          as={titleHeaderTag}
          id={listViewContainerTitleId}
          {...testIdProps('list-view-title')}
        >
          {title}
        </Heading>
      )}
      {/**
       * The `metadata` component might have a title rendered in it as well, so there can be some
       * duplication between it and the sr-only list-view-container-title Heading above. However, having that title
       * is important for accessibility and it's better to slightly over-describe than not. The `metadata`
       * section is optional while the title attribute and list-view-container-title that renders it are required.
       */}
      {metadata}
      <As
        className={clsx(styles.ul, itemsListClassName)}
        style={style}
        ref={containerRef}
        aria-labelledby={hasListItems ? listViewContainerTitleId : undefined}
        tabIndex={-1}
        // https://ui.githubapp.com/storybook/?path=/docs/recipes-listview-documentation-accessibility-listitem--docs#dom-declarations
        // The role="list" declaration helps enforce the list announcement,
        // which is key to understanding what the ListView's main content is, and how it can be interacted with.
        role={(role ?? hasListItems) ? 'list' : undefined}
        data-listview-component="items-list"
        data-density={variant}
        {...testIdProps('list-view-items')}
        {...rest}
      >
        {showEmptyState ? emptyState : children}
      </As>
    </div>
  )
}
